import React, { useRef } from 'react';
import { OfferType } from '../types/PartnerTypes';
import { format } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { FLAVOR } from '../../../Config/firebase';
interface MinimalOfferCarouselProps {
  offers: OfferType[];
  partnerId: string;
}

const MinimalOfferCarousel: React.FC<MinimalOfferCarouselProps> = ({ offers, partnerId }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleOfferClick = (offer: OfferType) => {
    if (FLAVOR === "DEVELOPMENT") {
      window.location.href = `https://plundrdev.hosaksham.com/details/${partnerId}/offer/${offer.id}`;
    } else {
      window.location.href = `https://www.goplundr.com/details/${partnerId}/offer/${offer.id}`;
    }

  };

  const formatTimestamp = (timestamp: Timestamp | undefined) => {
    if (!timestamp) return '';
    return format(timestamp.toDate(), 'MMM dd, yyyy');
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollAmount = container.clientWidth * 0.8; // Scroll 80% of container width
      const newScrollPosition = direction === 'left' 
        ? container.scrollLeft - scrollAmount 
        : container.scrollLeft + scrollAmount;
      
      container.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="mb-8 p-4 sm:p-6 md:p-10 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 rounded-xl sm:rounded-2xl md:rounded-3xl border border-amber-200/30 shadow-sm">
      <div className="relative mb-4 sm:mb-6 md:mb-10">
        <div className="flex items-start justify-between">
          <div className="relative pl-3 sm:pl-5">
            <div className="absolute -left-0 top-1/2 -translate-y-1/2 w-1 h-10 sm:h-12 md:h-16 bg-gradient-to-b from-amber-400 to-orange-500 rounded-full"></div>
            <div>
              <span className="text-orange-500 text-xs sm:text-sm font-medium mb-1 block">Limited Time</span>
              <h3 className="text-xl sm:text-xl font-rubik text-gray-900 mb-1 sm:mb-2 font-semibold tracking-wide">Special Offers</h3>
              <p className="text-gray-600 text-xs sm:text-sm">Exclusive deals on treasures</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => scroll('left')}
              className="p-2 rounded-full hover:bg-amber-100 transition-colors border border-amber-200 hover:border-orange-300 hover:scale-105 active:scale-95"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600">
                <path d="m15 18-6-6 6-6"/>
              </svg>
            </button>
            <button 
              onClick={() => scroll('right')}
              className="p-2 rounded-full hover:bg-amber-100 transition-colors border border-amber-200 hover:border-orange-300 hover:scale-105 active:scale-95"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600">
                <path d="m9 18 6-6-6-6"/>
              </svg>
            </button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-amber-300/50 via-orange-200/30 to-transparent"></div>
      </div>
      <div className="overflow-x-auto hide-scrollbar -mx-2 px-2" ref={scrollContainerRef}>
        <div className="flex gap-2 sm:gap-4 md:gap-3 pb-4">
          {offers.map((offer, index) => (
            <div
              key={index}
              onClick={() => handleOfferClick(offer)}
              className="flex-none w-[220px] sm:w-[260px] md:w-[260px] bg-white rounded-xl overflow-hidden border border-amber-300 shadow-sm hover:shadow-md hover:shadow-amber-100/40 transition-all duration-300 cursor-pointer group"
            >
              <div className="relative h-32 sm:h-40 overflow-hidden">
                <div className="absolute inset-0 transition-transform duration-500 group-hover:scale-110">
                  <img
                    src={offer.url}
                    alt={offer.BuzzName}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${
                  offer.isLive ? 'bg-amber-500/90' : 'bg-rose-500/90'
                } text-white backdrop-blur-sm z-10 shadow-lg`}>
                  {offer.isLive ? 'Active' : 'Expired'}
                </div>
                <div className="absolute inset-x-0 bottom-0 h-24 sm:h-28 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-[1]"></div>
                <div className="absolute bottom-2 left-3 right-3 z-[2]">
                  <h4 className="font-semibold text-sm sm:text-base text-white mb-0.5 line-clamp-1">{offer.BuzzName}</h4>
                  <p className="text-white/90 text-xs line-clamp-2">{offer.Description}</p>
                </div>
              </div>
              <div className="p-3 sm:p-4">
                <div className="space-y-2 sm:space-y-3">
                  {/* Offer Code */}
                  <div className="flex items-center gap-1.5 text-gray-800 text-xs bg-amber-100/70 p-1 sm:p-1.5 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600">
                      <path d="M20.91 8.84 8.56 2.23a1.93 1.93 0 0 0-1.81 0L3.1 4.13a2.12 2.12 0 0 0-.05 3.69l12.22 6.93a2 2 0 0 0 1.94 0L21 12.51a2.12 2.12 0 0 0-.09-3.67Z"></path>
                      <path d="m3.09 8.84 12.35-6.61a1.93 1.93 0 0 1 1.81 0l3.65 1.9a2.12 2.12 0 0 1 .1 3.69L8.73 14.75a2 2 0 0 1-1.94 0L3 12.51a2.12 2.12 0 0 1 .09-3.67Z"></path>
                      <line x1="12" y1="22" x2="12" y2="13"></line>
                    </svg>
                    <span className="line-clamp-1">Code: {offer.OfferCode}</span>
                  </div>

                  {/* Additional Description - Only show if needed and available */}
                  {offer.Description && (
                    <div className="text-xs text-gray-700">
                      <p className="line-clamp-2">{offer.Description}</p>
                    </div>
                  )}

                  {/* Offer Details - Only show if available */}
                  {(offer.StartDate || offer.EndDate) && (
                    <div className="text-[10px] sm:text-xs text-gray-600">
                      <span className="font-medium">Valid:</span>{' '}
                      <span>
                        {formatTimestamp(offer.StartDate)} - {formatTimestamp(offer.EndDate)}
                      </span>
                    </div>
                  )}

                  {/* Price and Action */}
                  <div className="flex items-center justify-between pt-1">
                    <div>
                      {offer.Price === 0 ? (
                        <span className="text-amber-600 price-free font-bold text-sm sm:text-base">Free</span>
                      ) : (
                        <>
                          <span className="text-amber-600 font-bold text-sm sm:text-base">₹{offer.Price}</span>
                          <span className="text-[10px] sm:text-xs text-gray-600 ml-1">only</span>
                        </>
                      )}
                    </div>
                    <button className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 sm:px-4 py-1.5 rounded-full text-xs font-medium hover:shadow-md hover:shadow-amber-200 active:scale-95 transition-all duration-300">
                      Claim Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MinimalOfferCarousel; 